import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import ReactCrop, { Crop } from 'react-image-crop';
import styled from '@emotion/styled';
import { useResizeImage, useTranslation, useUnmount } from 'src/hooks';
import { Typography } from 'src/components/atoms';
import { FlexColumn } from 'src/components/layout';
import { Dialog } from 'src/components/organisms';
import { useUploadUserAvatarMutation } from './useUploadUserAvatarMutation';
import 'react-image-crop/dist/ReactCrop.css';

const initialCropState: Partial<Crop> = { unit: '%', height: 100, aspect: 1, x: 0, y: 0 };

export interface PreviewImage {
  name: string;
  data: string;
}

interface Props {
  open: boolean;
  previewImage: PreviewImage;
  completedCrop: Crop | null;
  setCompletedCrop: Dispatch<SetStateAction<Crop | null>>;
  onClose: () => void;
  onCancelClose: () => void;
}

const HiddenCanvas = styled.canvas`
  display: none;
  object-fit: contain;
`;

export const ResizeUserAvatarDialog = ({
  open,
  previewImage,
  completedCrop,
  setCompletedCrop,
  onClose,
  onCancelClose
}: Props) => {
  const { t } = useTranslation();
  const imgRef = useRef<HTMLImageElement | null>(null);
  const hiddenCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [crop, setCrop] = useState<Partial<Crop>>(initialCropState);
  const [fileToBeSend, setFileToBeSend] = useState<File | null>(null);
  const uploadUserAvatar = useUploadUserAvatarMutation(onClose);

  const onLoad = useCallback(img => {
    imgRef.current = img;
  }, []);

  useResizeImage(completedCrop, imgRef, hiddenCanvasRef);

  useEffect(() => {
    if (!fileToBeSend) {
      return;
    }

    handleAddMediaFile();
  }, [fileToBeSend]);

  useUnmount(() => {
    setFileToBeSend(null);
    hiddenCanvasRef.current = null;
    imgRef.current = null;
  });

  const handleSetCroppedImage = async () => {
    if (hiddenCanvasRef.current) {
      hiddenCanvasRef.current.toBlob((blob: Blob | null) => {
        if (blob) {
          const file = new File([blob], previewImage.name, { type: 'image/jpeg' });
          setFileToBeSend(file);
        }
      }, 'image/jpeg');
    }
  };

  const handleAddMediaFile = async () => {
    const formData = new FormData();
    formData.append('file', fileToBeSend as File);
    uploadUserAvatar.mutate(formData);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('pages.mymes.editor.details_step.preview_image.dialog_title')}
      defaultActionButtons={{
        action: handleSetCroppedImage,
        cancelAction: onCancelClose,
        otherActionBtnProps: {
          loading: uploadUserAvatar.isLoading
        }
      }}
    >
      <FlexColumn alignItems='center'>
        <ReactCrop
          src={previewImage.data || ''}
          onImageLoaded={onLoad}
          crop={crop}
          onChange={setCrop}
          onComplete={setCompletedCrop}
        />
        <Typography variant='body2' sx={{ mt: 1.5 }}>
          {t('pages.mymes.editor.details_step.preview_image.dialog_description')}
        </Typography>
        <HiddenCanvas
          id='mymePreviewImage'
          ref={hiddenCanvasRef}
          style={{
            width: completedCrop?.width || 0,
            height: completedCrop?.height || 0
          }}
        />
      </FlexColumn>
    </Dialog>
  );
};
